/**
 * Player detail error state presentation component
 *
 * Renders a centered error state with an alert icon inside a tinted circle,
 * configurable title and subtitle, and a retry CTA button. Mirrors the
 * scouting ErrorState pattern (tokens, dark-mode variants, icon container) so
 * the player-detail screen shows a recoverable error + retry affordance
 * instead of a blank screen or a silent navigate-back when the player query
 * times out or fails (SE-5055).
 * @module features/player-detail/components/PlayerDetailErrorState/PlayerDetailErrorStateView
 */

import { AlertTriangleIcon, RefreshCwIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Icon } from "@/components/atoms/Icon";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

/** Props for the PlayerDetailErrorStateView presentation component */
interface PlayerDetailErrorStateViewProps {
  /** Error title displayed prominently */
  readonly title: string;
  /** Descriptive subtitle explaining the error */
  readonly subtitle: string;
  /** Callback fired when the retry button is pressed */
  readonly onRetry: () => void;
  /** Whether a retry is in flight — disables the CTA and shows a spinner */
  readonly isRetrying?: boolean;
}

/**
 * Renders a full-page error state with alert icon, message, and retry button
 * @param root0 - Component props
 * @param root0.title - Error heading text
 * @param root0.subtitle - Error description text
 * @param root0.onRetry - Handler for the retry CTA button
 * @param root0.isRetrying - Whether a retry is in progress (busy state)
 */
const PlayerDetailErrorStateView = ({
  title,
  subtitle,
  onRetry,
  isRetrying = false,
}: PlayerDetailErrorStateViewProps) => (
  // `alert` role so screen readers announce the error when it replaces the
  // player content rather than leaving the user on a silently-swapped screen.
  <Box accessibilityRole="alert" className="flex-1">
    <Stack className="flex-1 items-center justify-center gap-6 px-6 py-2">
      <Box className="items-center justify-center rounded-full bg-status-error-surface p-5">
        <Icon as={AlertTriangleIcon} size="xl" className="text-status-error" />
      </Box>
      <Stack className="items-center gap-2">
        <Text variant="title-lg" className="text-center text-content-primary">
          {title}
        </Text>
        <Text variant="body" className="text-center text-content-muted">
          {subtitle}
        </Text>
      </Stack>
      <Box className="pt-2">
        <Button
          label={isRetrying ? "Trying again…" : "Try again"}
          tone="secondary"
          variant="outline"
          size="md"
          labelTone="secondary"
          labelVariant="body"
          icon={RefreshCwIcon}
          iconTone="secondary"
          isLoading={isRetrying}
          isDisabled={isRetrying}
          onPress={onRetry}
        />
      </Box>
    </Stack>
  </Box>
);

PlayerDetailErrorStateView.displayName = "PlayerDetailErrorStateView";

export default memo(PlayerDetailErrorStateView);
